import { CircularProgress, makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { Field, Form } from 'react-final-form';
import { TenantDetails } from '../../api/auth';
import { BeforeUnload, Button, FormGroup } from '../common';
import { TextFieldWrapper } from './wrappers';
import { lengthField } from '../../utils/form';
import { useContext } from 'react';
import { UserContext } from '../../contexts/user-context';
import { isTablet } from '../../utils/functions';

interface TenantAccountFormProps {
  loading?: boolean;
  onSubmit: (formValues: any) => any;
  initialValue: FormValues;
  onResetPassword?: () => void;
}

export interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
}

export interface ParsedFormValues {
  values: TenantDetails;
  logoFile: File | null;
}

type Errors = {
  [K in keyof FormValues]?: string;
};

const useStyles = makeStyles((theme) => ({
  sectionBlock: {
    '& + &': {
      marginTop: 56,
    },
  },
  sectionTitle: {
    marginBottom: 32,
  },
  formBlocks: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  logoFormBlock: {
    margin: '65px 0 0 80px',
  },
  mainFormBlock: {
    width: '100%',

    [theme.breakpoints.up('sm')]: {
      width: 560,
    },
  },
  actionsBlock: {
    marginTop: 56,
    display: 'flex',
    gap: 16,
  },
  descriptionField: {
    minHeight: 64,
  },
}));

const validateForm = (values: FormValues) => {
  const errors: Errors = {};
  if (!values.email?.trim()) {
    errors.email = 'Required';
  }
  if (!values.firstName?.trim()) {
    errors.firstName = 'Required';
  }
  if (!values.lastName?.trim()) {
    errors.lastName = 'Required';
  }
  return errors;
};

function TenantAccountForm({
  loading = false,
  onSubmit,
  initialValue,
  onResetPassword,
}: TenantAccountFormProps) {
  const classes = useStyles();
  const { hasAccessToAction } = useContext(UserContext);

  const handleSubmit = (formValues: FormValues) => {
    return onSubmit(formValues);
  };

  return (
    <div>
      <Form
        validate={validateForm}
        onSubmit={handleSubmit}
        initialValues={initialValue}
        keepDirtyOnReinitialize
        render={(formProps) => (
          <div>
            <BeforeUnload
              when={formProps.dirty && !loading}
              title='Leave the page'
              body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
              disabled={loading}
              confirmButtonRenderer={({ onConfirm }) => (
                <Button
                  variant='outlined'
                  onClick={async () => {
                    await formProps.handleSubmit();
                    onConfirm();
                  }}
                  disabled={loading || !formProps.valid}>
                  {loading ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Save the changes'
                  )}
                </Button>
              )}
            />
            <div className={classes.mainFormBlock}>
              <form noValidate>
                <div className={classes.sectionBlock}>
                  {hasAccessToAction('settings.accountSettings.view') && (
                    <FormGroup mobile={isTablet()}>
                      <Field<string>
                        name='firstName'
                        component={TextFieldWrapper}
                        label='First name*'
                        testid='settings-form-firstname'
                        format={(value) => (value ? value.trim() : value)}
                        formatOnBlur
                        InputProps={{
                          readOnly: !hasAccessToAction(
                            'settings.accountSettings.update',
                          ),
                          inputProps: {
                            maxLength: lengthField.firstName,
                          },
                        }}
                      />
                      <Field<string>
                        name='lastName'
                        component={TextFieldWrapper}
                        testid='settings-form-lastname'
                        label='Last name*'
                        format={(value) => (value ? value.trim() : value)}
                        formatOnBlur
                        InputProps={{
                          readOnly: !hasAccessToAction(
                            'settings.accountSettings.update',
                          ),
                          inputProps: {
                            maxLength: lengthField.lastName,
                          },
                        }}
                      />
                    </FormGroup>
                  )}
                  <FormGroup>
                    <Field<string>
                      name='email'
                      label='Email*'
                      testid='settings-form-email'
                      component={TextFieldWrapper}
                      InputProps={{ readOnly: true }}
                    />
                  </FormGroup>
                </div>
                <div className={classes.actionsBlock}>
                  {onResetPassword && (
                    <Button
                      variant='outlined'
                      onClick={onResetPassword}
                      data-testid='reset-password-button'>
                      Reset Password
                    </Button>
                  )}
                  {hasAccessToAction('settings.accountSettings.update') && (
                    <Button
                      data-testid='settings-form-submit'
                      onClick={async () => {
                        await formProps.handleSubmit();
                      }}
                      disabled={loading || !formProps.valid}
                      startIcon={<CheckIcon />}>
                      {loading ? (
                        <CircularProgress size={24} color='inherit' />
                      ) : (
                        'Save'
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}
      />
    </div>
  );
}

export default TenantAccountForm;
